import React, { useState, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import MUNICIPIOS from '../data/divipolaMunicipios';
import './CityAutocomplete.css';

// Autocompletado de CIUDAD para el POS — patrón in-memory instantáneo (copiado del
// autocompletado de PRODUCTOS de SalesPage: filtro sobre un array embebido, sin ir al
// servidor; NO el de clientes, que es async/debounced). La lista es DIVIPOLA del DANE
// (src/data/divipolaMunicipios.js). Los nombres se repiten entre departamentos, por eso
// el ítem muestra «Municipio · Departamento».
//
// EL DROPDOWN VA EN UN PORTAL A `body`: el form de dirección vive dentro de `.pos-panel-body`
// (`overflow-y: auto`), y un dropdown `absolute` dentro de un ancestro con overflow queda
// RECORTADO (bug del 24-ago: no se veía nada). El portal lo saca del contexto de recorte;
// se posiciona por `getBoundingClientRect` y SIGUE al input en scroll/resize (rAF-throttled),
// con `visualViewport` para no quedar tapado por el teclado en móvil.
//
// LA MARCA: elegir de la lista estampa el código DANE (`dane`); escribir a mano NO. Esa es
// la única forma de distinguir después «canónico» de «texto libre» sin volver a normalizar
// en seis meses. La lógica vive en ciudadDesdeSeleccion / ciudadDesdeTextoLibre (puras).

const MAX_SUGERENCIAS = 8;
const ALTO_MAX = 300;
const MIN_ESPACIO_ABAJO = 160; // si abajo hay menos que esto (y arriba más), se abre HACIA ARRIBA

// Sin tildes + minúsculas + trim, para que «mede» encuentre «Medellín» y «cali» encuentre
// «Santiago de Cali» (match por substring, no por prefijo).
export function normalizarCiudad(s) {
  return String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

// Filtra la lista embebida. ≥2 chars; match por substring normalizado sobre el municipio.
export function filtrarMunicipios(query) {
  const q = normalizarCiudad(query);
  if (q.length < 2) return [];
  return MUNICIPIOS.filter((m) => normalizarCiudad(m.m).includes(q));
}

// Elegido de la lista → los TRES: ciudad canónica, departamento, y el código DANE (la marca).
export function ciudadDesdeSeleccion(muni) {
  return { city: muni.m, state: muni.d, dane: muni.c };
}

// Escrito a mano → el texto tal cual, SIN código (limpia la marca si venía de una selección
// previa). El departamento NO se toca acá: queda editable (decisión (a)).
export function ciudadDesdeTextoLibre(texto) {
  return { city: texto, dane: null };
}

export default function CityAutocomplete({ value, onPick, onFreeText, placeholder = 'Ciudad' }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const dropRef = useRef(null);
  const rafRef = useRef(0);

  const sugerencias = useMemo(() => filtrarMunicipios(value).slice(0, MAX_SUGERENCIAS + 1), [value]);
  const mostrar = open && sugerencias.length > 0;

  // Posición del dropdown a partir del input (viewport-relative → position: fixed). Decide
  // arriba/abajo según el espacio disponible y usa la altura de `visualViewport` (que en
  // móvil se achica con el teclado), así el dropdown no queda debajo del teclado ni fuera.
  const recomputar = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vh = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
    const abajo = vh - r.bottom;
    const arribaSpace = r.top;
    const haciaArriba = abajo < MIN_ESPACIO_ABAJO && arribaSpace > abajo;
    const maxHeight = Math.max(80, Math.min(ALTO_MAX, (haciaArriba ? arribaSpace : abajo) - 8));
    setPos({
      left: r.left, width: r.width, maxHeight,
      ...(haciaArriba ? { bottom: vh - r.top + 4 } : { top: r.bottom + 4 }),
    });
  }, []);

  // Mientras el dropdown está abierto: SEGUIR al input en scroll/resize (elegido sobre
  // cerrar). rAF-throttled. `true` en scroll captura el scroll de CUALQUIER ancestro (el
  // panel del POS), no sólo el de window; visualViewport cubre teclado y zoom en móvil.
  useLayoutEffect(() => {
    if (!mostrar) { setPos(null); return undefined; }
    recomputar();
    const onMove = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => { rafRef.current = 0; recomputar(); });
    };
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    const vv = window.visualViewport;
    if (vv) { vv.addEventListener('resize', onMove); vv.addEventListener('scroll', onMove); }
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
      if (vv) { vv.removeEventListener('resize', onMove); vv.removeEventListener('scroll', onMove); }
    };
  }, [mostrar, recomputar]);

  // Cerrar al clicar fuera — cuenta el input Y el dropdown PORTALEADO (que está fuera del wrap).
  useLayoutEffect(() => {
    if (!mostrar) return undefined;
    const handler = (e) => {
      if (wrapRef.current && wrapRef.current.contains(e.target)) return;
      if (dropRef.current && dropRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [mostrar]);

  const dropdown = mostrar && pos ? createPortal(
    <div
      ref={dropRef}
      className="ciudad-ac-drop"
      style={{
        position: 'fixed', left: pos.left, width: pos.width, maxHeight: pos.maxHeight,
        ...(pos.bottom != null ? { bottom: pos.bottom } : { top: pos.top }),
      }}
    >
      {sugerencias.slice(0, MAX_SUGERENCIAS).map((m) => (
        <button
          key={m.c}
          type="button"
          className="ciudad-ac-item"
          onMouseDown={(e) => { e.preventDefault(); onPick(m); setOpen(false); }}
        >
          <span className="ciudad-ac-muni">{m.m}</span>
          <span className="ciudad-ac-depto">{m.d}</span>
        </button>
      ))}
      {sugerencias.length > MAX_SUGERENCIAS && (
        <div className="ciudad-ac-more">Sigue escribiendo para acotar…</div>
      )}
    </div>,
    document.body,
  ) : null;

  return (
    <div className="ciudad-ac-wrap" ref={wrapRef}>
      <input
        ref={inputRef}
        className="pos-input"
        placeholder={placeholder}
        autoComplete="off"
        value={value}
        onChange={(e) => { onFreeText(e.target.value); setOpen(e.target.value.trim().length >= 2); }}
        onFocus={() => setOpen(normalizarCiudad(value).length >= 2)}
        onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } }}
      />
      {dropdown}
    </div>
  );
}
